import Logout from '@mui/icons-material/Logout';
import {
    AppBar,
    Box,
    IconButton,
    Link,
    Stack,
    Toolbar,
    Tooltip,
    Typography,
    alpha,
} from '@mui/material';
import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import packageInfo from '../../package.json';
import { useAuth } from '../contexts/AuthContext';
import { isMarketLandingEnabled, isStewardConsoleEnabled } from '../utils/featureFlags.js';

const NAV_TEXT_SPACING = '0.28em';
const MONTECH_FONT_STACK = '"Montech", "Roboto", "Helvetica", "Arial", sans-serif';

const stageLabel = (): string => {
  if (typeof window === 'undefined') return 'development';
  const stage = window.API_CONFIG?.stage;
  if (typeof stage === 'string' && stage.trim().length > 0) {
    return stage.trim();
  }
  return 'development';
};

const versionLabel = (): string => {
  const version = packageInfo?.version ?? '0.0.0';
  return `v.${version}`;
};

export function Header() {
  const { logout } = useAuth();
  const location = useLocation();
  const stewardEnabled = React.useMemo(() => isStewardConsoleEnabled(), []);
  const marketLandingEnabled = React.useMemo(() => isMarketLandingEnabled(), []);

  console.log('Header Debug:', {
    stewardEnabled,
    marketLandingEnabled,
    envSteward: (import.meta.env as any)?.VITE_STEWARD_CONSOLE,
    windowConfig: (window as any)?.API_CONFIG
  });

  const navItems = React.useMemo(() => {
    const items: Array<{ label: string; to: string }> = [];
    if (marketLandingEnabled) {
      items.push({ label: 'Market Dashboard', to: '/market' });
    }
    if (stewardEnabled) {
      items.push({ label: 'Prediction Markets', to: '/prediction-markets' });
    }
    return items;
  }, [marketLandingEnabled, stewardEnabled]);

  const isActiveLink = React.useCallback(
    (to: string) => {
      if (to === '/') {
        return location.pathname === '/';
      }
      return location.pathname === to || location.pathname.startsWith(`${to}/`);
    },
    [location.pathname]
  );

  const environmentDisplay = React.useMemo(() => {
    const stage = stageLabel().toUpperCase();
    const version = versionLabel().toUpperCase();
    return `${stage} : ${version}`;
  }, []);

  return (
    <AppBar position="static" elevation={0}
      sx={{
        backgroundColor: '#14121a',
        borderBottom: (theme) => `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
        boxShadow: '0 25px 60px rgba(2,4,15,0.65)',
        fontFamily: MONTECH_FONT_STACK,
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: 68,
          px: { xs: 2.5, md: 4 },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 2, md: 3 },
          fontFamily: MONTECH_FONT_STACK,
          position: 'relative',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ flexShrink: 0, minWidth: 0 }}
        >
          <BrandMark />
          <Typography
            variant="caption"
            sx={{
              fontSize: 12,
              letterSpacing: NAV_TEXT_SPACING,
              textTransform: 'uppercase',
              color: alpha('#d2d5db', 0.85),
              whiteSpace: 'nowrap',
              fontFamily: MONTECH_FONT_STACK,
            }}
          >
            Always know every move
          </Typography>
        </Stack>

        <Box
          component="nav"
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            justifyContent: 'center',
            gap: 3,
            flexWrap: 'wrap',
          }}
        >
          {navItems.map((item) => {
            const active = isActiveLink(item.to);
            return (
              <Link
                key={item.to}
                component={RouterLink}
                to={item.to}
                underline="none"
                sx={{
                  position: 'relative',
                  fontSize: 12,
                  letterSpacing: NAV_TEXT_SPACING,
                  textTransform: 'uppercase',
                  color: active ? '#d2d5db' : alpha('#d2d5db', 0.55),
                  transition: 'color 0.2s ease, opacity 0.2s ease',
                  px: 0.5,
                  fontFamily: MONTECH_FONT_STACK,
                  '&::after': active
                    ? {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: -26,
                        height: 2,
                        borderRadius: 999,
                        background: 'linear-gradient(90deg, rgba(144, 69, 235, 0.00) 0%, #9045EB 50.96%, rgba(144, 69, 235, 0.00) 100%)',
                      }
                    : undefined,
                  '&:hover': {
                    color: '#f5f7ff',
                  },
                }}
              >
                {item.label}
              </Link>
            );
          })}
        </Box>

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ ml: 'auto', flexShrink: 0, color: alpha('#bfc4d4', 0.8) }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: 12,
              textTransform: 'uppercase',
              letterSpacing: NAV_TEXT_SPACING,
              color: alpha('#6d6d87', 0.85),
              whiteSpace: 'nowrap',
              fontFamily: MONTECH_FONT_STACK,
            }}
          >
            {environmentDisplay}
          </Typography>
          <Tooltip title="Logout">
            <IconButton
              color="inherit"
              onClick={logout}
              size="small"
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                backgroundColor: alpha('#ffffff', 0.04),
                border: '1px solid',
                borderColor: alpha('#ffffff', 0.15),
                color: '#d2d5db',
                '&:hover': {
                  backgroundColor: alpha('#ffffff', 0.08),
                },
              }}
            >
              <Logout fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}

function BrandMark() {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        lineHeight: 0,
        color: '#d2d5db',
        svg: { height: 18, width: 'auto', display: 'block' },
      }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="110" height="18" viewBox="0 0 110 18" fill="none" role="img" aria-label="Akem">
        <path d="M8.5892 13.6606C7.3924 13.6606 6.24188 13.3895 5.17732 12.8539L5.96417 11.2736C6.78408 11.6835 7.6635 11.8885 8.5892 11.8885C9.5149 11.8885 10.3943 11.6835 11.2142 11.2736L12.0011 12.8539C10.9365 13.3895 9.786 13.6606 8.5892 13.6606Z" fill="#D2D5DB"/>
        <path d="M12.0341 5.75907H5.14426V7.52452H12.0341V5.75907Z" fill="#D2D5DB"/>
        <path d="M7.62799 3.21534L6.09907 4.09806L9.54401 10.0649L11.0729 9.18214L7.62799 3.21534Z" fill="#D2D5DB"/>
        <path d="M9.55 3.21561L6.10506 9.18242L7.63398 10.0651L11.0789 4.09834L9.55 3.21561Z" fill="#D2D5DB"/>
        <path d="M17.1784 17.2445H0V0.0661049H17.1784V17.2445ZM1.76545 15.4791H15.413V1.83155H1.76545V15.4791Z" fill="#D2D5DB"/>
        <path d="M35.2032 4.07309L42.4634 17.2445H44.7049L35.2032 0L25.7015 17.2445H27.943L35.2032 4.07309Z" fill="#D2D5DB"/>
        <path d="M110 0.0661049L101.563 9.01897L93.126 0.0661049V2.93578L101.563 11.8886L108.036 5.01861V17.2445H110V0.0661049Z" fill="#D2D5DB"/>
        <path d="M52.6923 8.6553L63.0867 0.0661049H60.0054L49.6045 8.6553L60.0054 17.2445H63.0867L52.6923 8.6553Z" fill="#D2D5DB"/>
        <path d="M84.8474 0.0661049H71.3719V2.02992H84.8474V0.0661049Z" fill="#D2D5DB"/>
        <path d="M84.8474 7.67013H71.3719V9.63394H84.8474V7.67013Z" fill="#D2D5DB"/>
        <path d="M84.8474 15.2807H71.3719V17.2445H84.8474V15.2807Z" fill="#D2D5DB"/>
      </svg>
    </Box>
  );
}
